import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { consumeMagicLink } from '../src/Utils/magicLinkApi';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Alert, AlertDescription, AlertTitle } from './ui/alert';
import { AlertCircle, CheckCircle, Loader2, LogIn } from 'lucide-react';
import { toast } from 'sonner';

/**
 * MagicLinkHandler Component
 * Handles magic link consumption and routing
 * - If user is logged in: redirects directly to target
 * - If user is not logged in: shows login, then redirects after login
 * - Handles expired, already-used, and invalid tokens
 */
export const MagicLinkHandler: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, user, signIn } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'expired' | 'used'>('loading');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [errorCode, setErrorCode] = useState<string>('');
  const [targetPath, setTargetPath] = useState<string>('');
  const [targetParams, setTargetParams] = useState<Record<string, any> | null>(null);
  const [requiresAuth, setRequiresAuth] = useState<boolean>(false);

  useEffect(() => {
    const handleMagicLink = async () => {
      const token = searchParams.get('t');
      const email = searchParams.get('email') || user?.email || undefined;

      if (!token) {
        setStatus('error');
        setErrorMessage('Magic link token is missing');
        setErrorCode('INVALID_TOKEN');
        return;
      }

      try {
        const result = await consumeMagicLink(token, email);

        if (result.success && result.data) {
          setTargetPath(result.data.targetPath);
          setTargetParams(result.data.targetParams || null);
          setRequiresAuth(result.data.requiresAuth);

          // If user is already logged in, redirect immediately
          if (isAuthenticated && !result.data.requiresAuth) {
            setStatus('success');
            // Build target URL with params
            const targetUrl = buildTargetUrl(result.data.targetPath, result.data.targetParams);
            // Small delay to show success message
            setTimeout(() => {
              navigate(targetUrl, { replace: true });
            }, 500);
          } else {
            // User needs to log in
            setRequiresAuth(true);
            setStatus('success');
            // Store target in sessionStorage for post-login redirect
            sessionStorage.setItem('magicLinkTarget', JSON.stringify({
              path: result.data.targetPath,
              params: result.data.targetParams,
            }));
          }
        }
      } catch (error: any) {
        console.error('Magic link consumption error:', error);
        
        if (error.code === 'EXPIRED') {
          setStatus('expired');
          setErrorMessage(error.message || 'This magic link has expired');
        } else if (error.code === 'ALREADY_USED') {
          setStatus('used');
          setErrorMessage(error.message || 'This magic link has already been used');
        } else if (error.code === 'INVALID_USER') {
          setStatus('error');
          setErrorMessage(error.message || 'This magic link is not valid for your account');
        } else {
          setStatus('error');
          setErrorMessage(error.message || 'Invalid or corrupted magic link');
        }
        setErrorCode(error.code || 'INVALID_TOKEN');
      }
    };

    handleMagicLink();
  }, [searchParams, isAuthenticated, user, navigate]);

  // Build target URL with query parameters
  const buildTargetUrl = (path: string, params?: Record<string, any>): string => {
    if (!params || Object.keys(params).length === 0) {
      return path;
    }

    const queryString = new URLSearchParams(
      Object.entries(params).reduce((acc, [key, value]) => {
        acc[key] = String(value);
        return acc;
      }, {} as Record<string, string>)
    ).toString();

    return `${path}${queryString ? `?${queryString}` : ''}`;
  };

  // Handle redirect after login
  useEffect(() => {
    if (isAuthenticated && requiresAuth && targetPath) {
      const storedTarget = sessionStorage.getItem('magicLinkTarget');
      if (storedTarget) {
        try {
          const { path, params } = JSON.parse(storedTarget);
          const targetUrl = buildTargetUrl(path, params);
          sessionStorage.removeItem('magicLinkTarget');
          navigate(targetUrl, { replace: true });
        } catch (error) {
          console.error('Error parsing stored target:', error);
          navigate(targetPath, { replace: true });
        }
      } else {
        navigate(targetPath, { replace: true });
      }
    }
  }, [isAuthenticated, requiresAuth, targetPath, navigate]);

  // Loading state
  if (status === 'loading') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
              <p className="text-sm text-gray-600">Validating magic link...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state - redirecting
  if (status === 'success' && isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6">
            <div className="flex flex-col items-center justify-center space-y-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <p className="text-sm text-gray-600">Redirecting to your tasks...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state - needs login
  if (status === 'success' && requiresAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <LogIn className="w-5 h-5" />
              Sign In Required
            </CardTitle>
            <CardDescription>
              Please sign in to access your tasks
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              After signing in, you will be redirected to your tasks automatically.
            </p>
            <Button
              onClick={() => navigate('/signIn')}
              className="w-full"
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Expired token
  if (status === 'expired') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-600">
              <AlertCircle className="w-5 h-5" />
              Link Expired
            </CardTitle>
            <CardDescription>
              This magic link has expired
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Expired Link</AlertTitle>
              <AlertDescription>
                {errorMessage || 'This magic link has expired. Please request a new one.'}
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/signIn')}
                className="flex-1"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex-1"
              >
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already used token
  if (status === 'used') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="w-5 h-5" />
              Link Already Used
            </CardTitle>
            <CardDescription>
              This magic link has already been consumed
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Link Already Used</AlertTitle>
              <AlertDescription>
                {errorMessage || 'This magic link can only be used once and has already been consumed.'}
              </AlertDescription>
            </Alert>
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => navigate('/signIn')}
                className="flex-1"
              >
                Sign In
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex-1"
              >
                Go to Dashboard
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-white flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-red-600">
            <AlertCircle className="w-5 h-5" />
            Invalid Link
          </CardTitle>
          <CardDescription>
            Unable to process magic link
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              {errorMessage || 'The magic link is invalid or corrupted. Please try again or contact support.'}
            </AlertDescription>
          </Alert>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/signIn')}
              className="flex-1"
            >
              Sign In
            </Button>
            <Button
              onClick={() => navigate('/dashboard')}
              className="flex-1"
            >
              Go to Dashboard
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

